import { ModelTransformer } from '../common/common.model';
import { padNum } from '../common/common.util';
import { PeriodType } from '../widget/date-range.widget.model';

import {
    CriteriaIdType,
    MetricData,
    MetricDataItem,
    PerformanceDataViewType,
    PerformanceDataViewTypeArgsMap,
} from './metric.pd.model';

export interface LevelDataMap {
    identifier: string;
    performanceDataViewType: PerformanceDataViewType;
    displayNameCriteriaIdType: CriteriaIdType;
    outputCriteriaType: CriteriaIdType; // TODO remove after old pi is removed
    dataId: number; // TODO remove after old pi is removed

    /* TODO not sure if to keep this or revert to outputCriteriaType. leave it for now */
    outputCriteriaTypes?: CriteriaIdType[]; // TODO make mandatory after old pi is removed
    outputCriteriaTypeArgs?: { [criteriaIdType: number]: number }; // TODO make mandatory after old pi is removed

    level?: any; // PathConfigLevel // TODO remove after old pi is removed
    dataItem?: MetricData; // TODO remove after old pi is removed
}

export interface ExtDataFilterOptions {
    date: string | Date;
    startDate: string | Date;
    userIds?: string;
    managerIds?: string;
    roleId?: number;
    superGroupIds?: string;
    queueIds?: string;
    peerGroupIds?: string;
    siteIds?: string;
    areaIds?: string;
    regionIds?: string;
    lobIds?: string;
    metricIds?: string;
    optionalColumns?: string;
}

export interface DataFilterOptions {
    identifier?: string;
    viewType: PerformanceDataViewType;
    displayNameCriteriaIdType: CriteriaIdType;
    outputCriteriaIdType: CriteriaIdType; // TODO remove after old pi is removed

    /* TODO not sure if to keep this or revert to outputCriteriaType. leave it for now */
    outputCriteriaIdTypes?: CriteriaIdType[]; // TODO make mandatory after old pi is removed

    inputCriteriaIdTypes?: CriteriaIdType[]; // TODO make mandatory after old pi is removed
    inputCriteriaIdTypeArgs?: string[];
    periodType: PeriodType;
    date: Date;
    startDate?: Date;
    roleId?: number;
    lobIdList?: number[];
    regionIdList?: number[];
    areaIdList?: number[];
    siteIdList?: number[];
    peerGroupIdList?: number[];
    superGroupIdList?: number[];
    queueIdList?: number[];
    managerIdList?: number[];
    userIdList?: number[];
    metricIdList?: number[];
    optionalColumnsList?: number[];
}

function join(list: number[]): string {
    if (!list) return null;
    return list.filter((i) => i != null).join(',');
}

export class DataFilterOptionsModelTransformer
    implements ModelTransformer<DataFilterOptions, ExtDataFilterOptions>
{
    reverseTransform(model: DataFilterOptions): ExtDataFilterOptions {
        const transformed: ExtDataFilterOptions = {
            date: model.date,
            startDate: model.startDate,
            userIds: join(model.userIdList),
            managerIds: join(model.managerIdList),
            roleId: model.roleId,
            superGroupIds: join(model.superGroupIdList),
            queueIds: join(model.queueIdList),
            peerGroupIds: join(model.peerGroupIdList),
            siteIds: join(model.siteIdList),
            areaIds: join(model.areaIdList),
            regionIds: join(model.regionIdList),
            lobIds: join(model.lobIdList),
            metricIds: join(model.metricIdList),
            optionalColumns: null, // TODO revise. does nothing
        };

        let date = transformed.date as Date;
        if (!date || isNaN(date.getTime())) {
            throw new Error('invalid date');
        }
        transformed.date = `${date.getFullYear()}-${padNum(
            date.getMonth() + 1,
            2,
        )}-${padNum(date.getDate(), 2)}`;

        date = transformed.startDate as Date;
        if (!date || isNaN(date.getTime())) {
            transformed.startDate = null;
        } else {
            transformed.startDate = `${date.getFullYear()}-${padNum(
                date.getMonth() + 1,
                2,
            )}-${padNum(date.getDate(), 2)}`;
        }

        return transformed;
    }
}

export function generateDataFilterOptions(
    opts: DataFilterOptions,
    parentLevelsDataMap?: LevelDataMap[],
): DataFilterOptions {
    const outOpts: DataFilterOptions = {
        ...opts,
        date: new Date(opts.date),
        startDate: new Date(opts.startDate),
        lobIdList: [...(opts.lobIdList || [])],
        regionIdList: [...(opts.regionIdList || [])],
        areaIdList: [...(opts.areaIdList || [])],
        siteIdList: [...(opts.siteIdList || [])],
        peerGroupIdList: [...(opts.peerGroupIdList || [])],
        superGroupIdList: [...(opts.superGroupIdList || [])],
        queueIdList: [...(opts.queueIdList || [])],
        managerIdList: [...(opts.managerIdList || [])],
        userIdList: [...(opts.userIdList || [])],
        metricIdList: [...(opts.metricIdList || [])],
        optionalColumnsList: [...(opts.optionalColumnsList || [])],
    };
    if (parentLevelsDataMap) {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const mapViewType = (
            levelData: LevelDataMap,
            primaryOptsKey: string,
            secondaryOptsKey: string,
            tertiaryOptsKey: string,
            optss: DataFilterOptions,
        ) => {
            const viewTypeMap =
                PerformanceDataViewTypeArgsMap[
                    levelData.performanceDataViewType
                ];
            const map = {
                [viewTypeMap.primary]: primaryOptsKey,
                [viewTypeMap.secondary]: secondaryOptsKey,
                [viewTypeMap.tertiary]: tertiaryOptsKey,
            };

            // TODO remove after old pi is removed
            const keyOld = map[levelData.outputCriteriaType];
            if (keyOld == null) {
                return;
            }
            optss[keyOld].push(levelData.dataId);

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            levelData.outputCriteriaTypes &&
                levelData.outputCriteriaTypes.forEach((e) => {
                    const key = map[e];
                    if (key != null) {
                        optss[key].push(levelData.outputCriteriaTypeArgs[e]);
                    }
                });
        };
        parentLevelsDataMap
            .filter((e) => e.identifier === outOpts.identifier)
            .forEach((value) => {
                if (
                    value.outputCriteriaType === CriteriaIdType.USER_ROLE_ID ||
                    value.outputCriteriaType === CriteriaIdType.TEAM_ROLE_ID
                ) {
                    outOpts.roleId = value.dataId;
                    return;
                }

                switch (value.performanceDataViewType) {
                    case PerformanceDataViewType.MARKET:
                        break;
                    case PerformanceDataViewType.LOB:
                        outOpts.lobIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.REGION_LOB:
                        mapViewType(
                            value,
                            'regionIdList',
                            'lobIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.AREA_LOB:
                        mapViewType(
                            value,
                            'areaIdList',
                            'lobIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SITE_LOB:
                        mapViewType(
                            value,
                            'siteIdList',
                            'lobIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.REGION:
                        outOpts.regionIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.AREA:
                        outOpts.areaIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.SITE:
                        outOpts.siteIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.PEER_GROUP:
                        outOpts.peerGroupIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.REGION_PEER_GROUP:
                        mapViewType(
                            value,
                            'regionIdList',
                            'peerGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.AREA_PEER_GROUP:
                        mapViewType(
                            value,
                            'areaIdList',
                            'peerGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SITE_PEER_GROUP:
                        mapViewType(
                            value,
                            'siteIdList',
                            'peerGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SUPER_GROUP:
                        outOpts.superGroupIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.REGION_SUPER_GROUP:
                        mapViewType(
                            value,
                            'regionIdList',
                            'superGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.AREA_SUPER_GROUP:
                        mapViewType(
                            value,
                            'areaIdList',
                            'superGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SITE_SUPER_GROUP:
                        mapViewType(
                            value,
                            'siteIdList',
                            'superGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.TEAM_SUPER_GROUP:
                        mapViewType(
                            value,
                            'managerIdList',
                            'superGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.USER_SUPER_GROUP:
                        mapViewType(
                            value,
                            'userIdList',
                            'superGroupIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SITE_SUPER_GROUP_QUEUE:
                        mapViewType(
                            value,
                            'siteIdList',
                            'superGroupIdList',
                            'queueIdList',
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.SUPER_GROUP_QUEUE:
                        mapViewType(
                            value,
                            'superGroupIdList',
                            'queueIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.USER_QUEUE:
                        mapViewType(
                            value,
                            'userIdList',
                            'queueIdList',
                            null,
                            outOpts,
                        );
                        break;
                    case PerformanceDataViewType.USER:
                        outOpts.userIdList.push(value.dataId);
                        break;
                    case PerformanceDataViewType.QUEUE:
                        outOpts.queueIdList.push(value.dataId);
                        break;
                }
            });
    }
    return outOpts;
}

export function createMetricDataItem(values: {
    id: number;
    name?: string;
    shortName?: string;
    initials?: string;
    weight?: number;
}): MetricDataItem {
    return {
        id: values.id,
        name: values.name,
        shortName: values.shortName,
        initials: values.initials,
        eventDate: null,
        actual: null,
        target: null,
        previousActual: null,
        projectedActual: null,
        projectedTarget: null,
        projectedPctToTgt: null,
        gap: null,
        goal: null,
        rankOrder: null,
        targetThresholdColor: null,
        targetThresholdBold: null,
        targetThresholdName: null,
        trendClass: null,
        trendName: null,
        displayId: null,
        displayName: null,
        displayShortName: null,
        displayInitials: null,
        dataId: null,
        dataIds: null,
        primaryId: null,
        secondaryId: null,
        tertiaryId: null,
        // last mile
        percentToTarget: null,
        objectiveMet: null,
        weight: values.weight,
        weightedScore: null,
    };
}

export function padMissingMetrics(
    metrics: MetricDataItem[],
    expectedMetrics: number[],
): MetricDataItem[] {
    const ret = [];
    expectedMetrics.forEach((value2) => {
        const metric = metrics.find((value3) => value3.id === value2);
        ret.push(metric || createMetricDataItem({ id: value2 }));
    });
    return ret;
}

/// /////////////////

// PIConfig.Lookup_PiViewAttributes TODO rename to DataConfig.Lookup_PerformanceDataAttributes
export const Attributes: { [key: string]: string } = {
    AUTO_EXPAND_LEVEL: 'autoexpandlevel',
    AUTO_EXPAND_PRIMARY_ID: 'autoexpandprimaryid',
    AUTO_EXPAND_SECONDARY_ID: 'autoexpandsecondaryid',
    AUTO_EXPAND: 'autoexpand',
    DEFAULT_LOB_ID: 'defaultlobid',
    DEFAULT_REGION_ID: 'defaultregionid',
    DEFAULT_AREA_ID: 'defaultareaid',
    DEFAULT_SITE_ID: 'defaultsiteid',
    DEFAULT_PEER_GROUP_ID: 'defaultpeergroupid',
    DEFAULT_SUPER_GROUP_ID: 'defaultsupergroupid',
    DEFAULT_QUEUE_ID: 'defaultqueueid',
    EXCLUDED_LOB_ID: 'excludedlobid',
    EXCLUDED_REGION_ID: 'excludedregionid',
    EXCLUDED_AREA_ID: 'excludedareaid',
    EXCLUDED_SITE_ID: 'excludedsiteid',
    EXCLUDED_PEER_GROUP_ID: 'excludedpeergroupid',
    EXCLUDED_SUPER_GROUP_ID: 'excludedsupergroupid',
    EXCLUDED_QUEUE_ID: 'excludedqueueid',
    INCLUDED_LOB_ID: 'includedlobid',
    INCLUDED_REGION_ID: 'includedregionid',
    INCLUDED_AREA_ID: 'includedareaid',
    INCLUDED_SITE_ID: 'includedsiteid',
    INCLUDED_PEER_GROUP_ID: 'includedpeergroupid',
    INCLUDED_SUPER_GROUP_ID: 'includedsupergroupid',
    INCLUDED_QUEUE_ID: 'includedqueueid',
    LOCK_TO_USER_PROFILE: 'locktouserprofile',
    SITE_IS_THIRD_PARTY: 'site.isthirdparty',
};

export interface AutoExpandConfig {
    level?: number; // TODO remove after old pi is removed
    levelId?: string; // TODO make mandatory after old pi is removed
    primaryId: number;
    secondaryId: number;
    tertiaryId: number;
}

export interface FilterContext {
    autoExpand?: AutoExpandConfig[];
    defaultLobId?: number;
    defaultRegionId?: number;
    defaultAreaId?: number;
    defaultSiteId?: number;
    defaultPeerGroupId?: number;
    defaultSuperGroupId?: number;
    defaultQueueId?: number;
    excludedLobIds: number[];
    excludedRegionIds: number[];
    excludedAreaIds: number[];
    excludedSiteIds: number[];
    excludedPeerGroupIds: number[];
    excludedSuperGroupIds: number[];
    excludedQueueIds: number[];
    includedLobIds: number[];
    includedRegionIds: number[];
    includedAreaIds: number[];
    includedSiteIds: number[];
    includedPeerGroupIds: number[];
    includedSuperGroupIds: number[];
    includedQueueIds: number[];
    lockToUserProfile?: boolean;
    siteIsThirdParty?: boolean;
}

export function parseContextAttrs<
    TAttribute extends { name: string; value: string },
>(pathAttrs: TAttribute[]): FilterContext {
    const ctx: FilterContext = {
        autoExpand: [],
        excludedLobIds: [],
        excludedRegionIds: [],
        excludedAreaIds: [],
        excludedSiteIds: [],
        excludedPeerGroupIds: [],
        excludedSuperGroupIds: [],
        excludedQueueIds: [],
        includedLobIds: [],
        includedRegionIds: [],
        includedAreaIds: [],
        includedSiteIds: [],
        includedPeerGroupIds: [],
        includedSuperGroupIds: [],
        includedQueueIds: [],
    };
    if (!pathAttrs) return ctx;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const pushIfNotExists = (attr, key) => {
        if (!attr.value) return;
        attr.value.split(',').forEach((id) => {
            const val = parseInt(id);
            if (ctx[key].indexOf(val) === -1) {
                ctx[key].push(val);
            }
        });
    };
    pathAttrs.forEach((attr) => {
        if (attr.name === Attributes.AUTO_EXPAND) {
            if (attr.value) {
                const parts = attr.value.split('|');
                if (parts.length >= 2) {
                    ctx.autoExpand.push({
                        levelId: parts[0],
                        primaryId: parseInt(parts[1]),
                        secondaryId:
                            parts.length >= 3 ? parseInt(parts[2]) : -999,
                        tertiaryId:
                            parts.length >= 4 ? parseInt(parts[3]) : -999,
                    });
                }
            }
        }
        if (attr.name === Attributes.DEFAULT_LOB_ID) {
            ctx.defaultLobId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_REGION_ID) {
            ctx.defaultRegionId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_AREA_ID) {
            ctx.defaultAreaId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_SITE_ID) {
            ctx.defaultSiteId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_PEER_GROUP_ID) {
            ctx.defaultPeerGroupId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_SUPER_GROUP_ID) {
            ctx.defaultSuperGroupId = parseInt(attr.value);
        }
        if (attr.name === Attributes.DEFAULT_QUEUE_ID) {
            ctx.defaultQueueId = parseInt(attr.value);
        }
        if (attr.name === Attributes.EXCLUDED_LOB_ID) {
            pushIfNotExists(attr, 'excludedLobIds');
        }
        if (attr.name === Attributes.EXCLUDED_REGION_ID) {
            pushIfNotExists(attr, 'excludedRegionIds');
        }
        if (attr.name === Attributes.EXCLUDED_AREA_ID) {
            pushIfNotExists(attr, 'excludedAreaIds');
        }
        if (attr.name === Attributes.EXCLUDED_SITE_ID) {
            pushIfNotExists(attr, 'excludedSiteIds');
        }
        if (attr.name === Attributes.EXCLUDED_PEER_GROUP_ID) {
            pushIfNotExists(attr, 'excludedPeerGroupIds');
        }
        if (attr.name === Attributes.EXCLUDED_SUPER_GROUP_ID) {
            pushIfNotExists(attr, 'excludedSuperGroupIds');
        }
        if (attr.name === Attributes.EXCLUDED_QUEUE_ID) {
            pushIfNotExists(attr, 'excludedQueueIds');
        }
        if (attr.name === Attributes.INCLUDED_LOB_ID) {
            pushIfNotExists(attr, 'includedLobIds');
        }
        if (attr.name === Attributes.INCLUDED_REGION_ID) {
            pushIfNotExists(attr, 'includedRegionIds');
        }
        if (attr.name === Attributes.INCLUDED_AREA_ID) {
            pushIfNotExists(attr, 'includedAreaIds');
        }
        if (attr.name === Attributes.INCLUDED_SITE_ID) {
            pushIfNotExists(attr, 'includedSiteIds');
        }
        if (attr.name === Attributes.INCLUDED_PEER_GROUP_ID) {
            pushIfNotExists(attr, 'includedPeerGroupIds');
        }
        if (attr.name === Attributes.INCLUDED_SUPER_GROUP_ID) {
            pushIfNotExists(attr, 'includedSuperGroupIds');
        }
        if (attr.name === Attributes.INCLUDED_QUEUE_ID) {
            pushIfNotExists(attr, 'includedQueueIds');
        }
        if (attr.name === Attributes.LOCK_TO_USER_PROFILE) {
            ctx.lockToUserProfile = parseInt(attr.value) === 1;
        }
        if (attr.name === Attributes.SITE_IS_THIRD_PARTY) {
            ctx.siteIsThirdParty = parseInt(attr.value) === 1;
        }
    });
    return ctx;
}
